// ============================================
// SEVEN STEP BREAKDOWN COMPONENT (V3.1)
// Expandable cards with full detail view
// ============================================

import React, { useState } from 'react';
import { TickerAnalysis } from '../types';
import {
  Activity, BarChart2, DollarSign, Shield, TrendingUp, Target, PieChart,
  CheckCircle, ChevronDown, ChevronUp, X
} from 'lucide-react';

interface SevenStepBreakdownProps {
  scores: TickerAnalysis['scores'];
}

export const SevenStepBreakdown: React.FC<SevenStepBreakdownProps> = ({ scores }) => {
  const [expandedStep, setExpandedStep] = useState<string | null>(null);

  // Define the steps to display in the grid
  const steps = [
    { key: 'step1_sentiment', label: 'Sentiment', icon: <Activity size={16} />, description: 'Market mood and sector performance' },
    { key: 'step2_technicals', label: 'Technicals', icon: <BarChart2 size={16} />, description: 'Price action, RSI, MACD, moving averages' },
    { key: 'step3_options', label: 'Liquidity', icon: <DollarSign size={16} />, description: 'Options volume, bid-ask spread, open interest' },
    { key: 'step4_risk', label: 'Risk Check', icon: <Shield size={16} />, description: 'Earnings, events, volatility risks' },
    { key: 'step5_greeks', label: 'The Greeks', icon: <TrendingUp size={16} />, description: 'Delta, theta, IV rank analysis' },
    { key: 'step6_entry', label: 'Entry/Exit', icon: <Target size={16} />, description: 'Optimal entry points and targets' },
    { key: 'step8_sizing', label: 'Pos. Sizing', icon: <PieChart size={16} />, description: 'Recommended position size and risk' },
  ];

  const toggleExpand = (key: string) => {
    setExpandedStep(expandedStep === key ? null : key);
  };

  return (
    <div className="bg-terminal-card border border-terminal-border rounded-xl p-6 mt-6 shadow-2xl">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-sm font-bold text-terminal-dim uppercase tracking-widest flex items-center gap-2">
          <CheckCircle size={14} className="text-cyan-400" />
          7-Step Scoring Breakdown
        </h3>
        <span className="text-xs text-terminal-dim font-mono">
          Total Raw: {scores.total} pts | Click to expand
        </span>
      </div>

      {/* Responsive Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {steps.map((step) => {
          const scoreData = scores[step.key as keyof typeof scores];

          if (typeof scoreData === 'number' || !scoreData) return null;

          const percent = scoreData.maxPoints > 0
            ? Math.round((scoreData.points / scoreData.maxPoints) * 100)
            : 0;

          const colorClass = percent >= 75 ? 'bg-emerald-500'
            : percent >= 50 ? 'bg-amber-500'
            : 'bg-rose-500';

          const textClass = percent >= 75 ? 'text-emerald-400'
            : percent >= 50 ? 'text-amber-400'
            : 'text-rose-400';

          const borderClass = percent >= 75 ? 'border-emerald-500/30'
            : percent >= 50 ? 'border-amber-500/30'
            : 'border-rose-500/30';

          const isExpanded = expandedStep === step.key;

          return (
            <div
              key={step.key}
              className={`bg-slate-900/50 border rounded-xl transition-all duration-300 cursor-pointer ${
                isExpanded
                  ? `${borderClass} col-span-1 sm:col-span-2 lg:col-span-2 row-span-2`
                  : 'border-slate-800 hover:border-slate-700'
              }`}
              onClick={() => toggleExpand(step.key)}
            >
              {/* Compact View */}
              <div className="p-4">
                {/* Step Header */}
                <div className="flex justify-between items-start mb-3">
                  <div className="flex items-center gap-2 text-slate-300 text-xs font-bold uppercase tracking-wider">
                    {step.icon}
                    {step.label}
                  </div>
                  <div className="flex items-center gap-2">
                    <div className={`font-mono font-bold text-lg ${textClass}`}>
                      {scoreData.points}
                      <span className="text-slate-300 text-xs ml-0.5">/{scoreData.maxPoints}</span>
                    </div>
                    {isExpanded ? (
                      <ChevronUp size={14} className="text-slate-300" />
                    ) : (
                      <ChevronDown size={14} className="text-slate-300" />
                    )}
                  </div>
                </div>

                {/* Progress Bar */}
                <div className="w-full h-1.5 bg-slate-950 rounded-full overflow-hidden mb-2">
                  <div
                    className={`h-full rounded-full transition-all duration-1000 ${colorClass}`}
                    style={{ width: `${percent}%` }}
                  />
                </div>

                {/* Score percentage */}
                <div className="flex justify-between items-center text-xs">
                  <span className="text-slate-300">{step.description}</span>
                  <span className={`font-bold ${textClass}`}>{percent}%</span>
                </div>
              </div>

              {/* Expanded Details */}
              {isExpanded && (
                <div className="px-4 pb-4 border-t border-slate-800 mt-2 pt-4 animate-fadeIn">
                  <div className="flex justify-between items-center mb-3">
                    <div className="text-sm text-slate-300 uppercase font-bold">
                      Detailed Analysis
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedStep(null);
                      }}
                      className="p-1 hover:bg-slate-800 rounded transition-colors"
                    >
                      <X size={14} className="text-slate-300" />
                    </button>
                  </div>

                  {/* All Details - Scrollable if needed */}
                  <div className="max-h-64 overflow-y-auto pr-2 custom-scrollbar">
                    <ul className="space-y-2">
                      {scoreData.details.map((detail, i) => {
                        // Determine if this is a positive or negative indicator
                        const isNegative = detail.toLowerCase().includes('low') ||
                                          detail.toLowerCase().includes('fail') ||
                                          detail.toLowerCase().includes('warning') ||
                                          detail.toLowerCase().includes('risk') ||
                                          detail.toLowerCase().includes('bearish') ||
                                          detail.toLowerCase().includes('overbought') ||
                                          detail.toLowerCase().includes('oversold');
                        const isPositive = detail.toLowerCase().includes('strong') ||
                                          detail.toLowerCase().includes('bullish') ||
                                          detail.toLowerCase().includes('pass') ||
                                          detail.toLowerCase().includes('good') ||
                                          detail.toLowerCase().includes('high') ||
                                          detail.toLowerCase().includes('optimal');

                        return (
                          <li
                            key={i}
                            className={`text-sm flex items-start gap-2 leading-relaxed p-2 rounded-lg ${
                              isNegative ? 'bg-rose-500/10 text-rose-300' :
                              isPositive ? 'bg-emerald-500/10 text-emerald-300' :
                              'bg-slate-800/50 text-slate-300'
                            }`}
                          >
                            <span className={`mt-1 shrink-0 text-xs ${
                              isNegative ? 'text-rose-500' :
                              isPositive ? 'text-emerald-500' :
                              'text-cyan-500'
                            }`}>
                              {isNegative ? '⚠' : isPositive ? '✓' : '•'}
                            </span>
                            {detail}
                          </li>
                        );
                      })}
                    </ul>
                  </div>

                  {/* Score Interpretation */}
                  <div className={`mt-4 p-3 rounded-lg ${
                    percent >= 75 ? 'bg-emerald-500/10 border border-emerald-500/30' :
                    percent >= 50 ? 'bg-amber-500/10 border border-amber-500/30' :
                    'bg-rose-500/10 border border-rose-500/30'
                  }`}>
                    <div className="text-xs font-bold uppercase mb-1 text-slate-300">
                      Score Interpretation
                    </div>
                    <div className={`text-sm font-medium ${textClass}`}>
                      {percent >= 75 ? '✓ Strong - This factor supports the trade' :
                       percent >= 50 ? '⚠ Moderate - Proceed with caution' :
                       '✗ Weak - This factor is a concern'}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Overall Score Summary */}
      <div className="mt-6 p-4 bg-slate-900/50 rounded-xl border border-slate-800">
        <div className="flex justify-between items-center">
          <div>
            <div className="text-sm text-slate-300 uppercase font-bold mb-1">Overall Framework Score</div>
            <div className="text-2xl font-bold text-white">
              {scores.normalizedTotal}<span className="text-slate-300 text-lg">/100</span>
            </div>
          </div>
          <div className={`px-4 py-2 rounded-lg text-sm font-bold ${
            scores.normalizedTotal >= 70 ? 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30' :
            scores.normalizedTotal >= 50 ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' :
            'bg-rose-500/20 text-rose-400 border border-rose-500/30'
          }`}>
            {scores.normalizedTotal >= 70 ? 'TRADE APPROVED' :
             scores.normalizedTotal >= 50 ? 'CAUTION' :
             'NO TRADE'}
          </div>
        </div>
      </div>
    </div>
  );
};
